import Button from "@/components/atoms/Button";
import { useForm } from "react-hook-form";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/atoms/Form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import Select from "@/components/molecules/Select";
import { useState } from 'react';
import FileUpload from "@/components/atoms/FileUpload";
import Ticket from "@/types/Ticket";
import { useEffect } from 'react';
import {TagInput, Tag} from "@/components/atoms/TagInput";
import CKEditorWithFileUpload from "@/components/atoms/CKEditor";

const getFormSchema = () =>
  z
    .object({
      cc: z.array(z.string()).optional(),
      message: z.string().min(1, "Message is required"),
      ticket_status_id: z.string(),
      files: z.array(z.any()),
    })

type formData = z.infer<ReturnType<typeof getFormSchema>>;

type Props = {
  ticket: Ticket;
  onSubmit: (data: formData) => void;
  isSubmitting?: boolean;
  currentUser?: any;
  defaultValues?: Partial<formData>;
};

export const useReplyForm = () =>
  useForm<formData>({
    resolver: zodResolver(getFormSchema()),
    defaultValues: {
      cc: [],
      message: "",
      ticket_status_id: "",
      files: []
    },
  });


const ReplyForm = ({
  ticket,
  onSubmit,
  isSubmitting,
  currentUser,
  defaultValues
} : Props) => {
  const form = useReplyForm();
  const [editorData, setEditorData] = useState(defaultValues?.message || "");
  const [fileUploadKey, setFileUploadKey] = useState(0);
  const [selectedTicketStatus, setSelectedTicketStatus] = useState<string>(ticket.status.id.toString());
  const [attachmentUploading, setAttachmentUploading] = useState(false);
  const [tags, setTags] = useState<Tag[]>(
    defaultValues?.cc?.map((email, index) => ({
      id: index.toString(),
      text: email,
    })) || []
  );

  const handleSubmit = async (data: formData) => {
    onSubmit({
      ...data,
      ticket_status_id: selectedTicketStatus,
      cc: tags.map((tag) => tag.text),
    });
  };

  const handleEditorChange = (data: string) => {
    setEditorData(data);
    form.setValue('message', data);
  };

  useEffect(() => {
    if (defaultValues) {
      form.reset({
        ...defaultValues,
        ticket_status_id: ticket.status.id.toString(),
      });
      setEditorData(defaultValues.message || "");
      setTags(
        defaultValues.cc?.map((email, index) => ({
          id: index.toString(),
          text: email,
        })) || []
      );
    }
  }, [defaultValues]);

  return (
    <div className="flex justify-end">
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(handleSubmit)}
          className="space-y-4 w-full"
        >
          <div className="flex gap-12">
            <FormField
              control={form.control}
              name="cc"
              render={({ field }) => {
                useEffect(() => {
                  field.onChange(tags.map((tag) => tag.text));
                }, [tags]);

                return (
                  <FormItem className="w-2/3">
                    <FormLabel>CC</FormLabel>
                    <FormControl>
                      <TagInput tags={tags} setTags={setTags} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                );
              }}
            />

            {currentUser && !currentUser.roles.some((role:any) => role.name === "client") ? (
              <FormField
                control={form.control}
                name="ticket_status_id"
                render={({ field }) => (
                  <FormItem className="w-1/3">
                    <FormLabel>Ticket Status</FormLabel>
                    <FormControl>
                      <Select
                        options={ticket.account.ticket_statuses.map((status) => ({
                          label: status.name,
                          value: status.id.toString(),
                        }))}
                        value={selectedTicketStatus}
                        onChange={(value) => {
                          setSelectedTicketStatus(value);
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            ) : (
              <input
                type="hidden"
                name="ticket_status_id"
                value='4'
                ref={(input) => {
                  if (input) {
                    form.setValue("ticket_status_id", '4');
                  }
                }}
              />
            )}
            
          </div>
            


          <FormField
            control={form.control}
            name="message"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormLabel></FormLabel>
                <FormControl>
                  <CKEditorWithFileUpload
                    value={editorData}
                    onChange={handleEditorChange}
                    isPublic={true}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          
          <div className="flex gap-12 w-full items-start">
            <div className="w-full">
              <FormField
                control={form.control}
                name="files"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Attachments</FormLabel>
                    <FormControl>
                      <FileUpload
                        key={fileUploadKey}
                        files={field.value || []}
                        onChange={(files) => field.onChange(files)}
                        setAttachmentUploading={setAttachmentUploading}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="flex w-full justify-end">
              <Button type="submit" disabled={isSubmitting || attachmentUploading} className="mt-6">
                {isSubmitting ? "Submitting..." : "Submit"}
              </Button>
            </div>
          </div>
          
        </form>
      </Form>
    </div>
  );
};

export default ReplyForm;
